import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import {
  Pane,
  Card,
  Badge,
  Text,
  Heading,
  IconButton,
  Spinner,
  Button,
  Tab,
  Tablist,
  CrossIcon,
  TextInput,
  Textarea,
  EditIcon,
  TrashIcon,
  Dialog,
  TickIcon,
  Strong,
  Image
} from 'evergreen-ui';
import MDEditor from '@uiw/react-md-editor';
import api from '../../services/api';
import { toaster } from 'evergreen-ui';
import { getSeverityBackgroundColor, getSeverityTextColor } from './utils';

const EntityOverviewPanel = ({
  selectedEntity,
  entityOverview,
  setEntityOverview,
  overviewLoading,
  entityDetails,
  sidePanelTab,
  setSidePanelTab,
  wstgChecklist,
  wstgLoading,
  editingWstgItem,
  setEditingWstgItem,
  wstgItemNotes,
  setWstgItemNotes,
  showLinkFindingDialog,
  setShowLinkFindingDialog,
  linkFindingItem,
  availableFindings,
  findingsLoading,
  notes,
  notesLoading,
  selectedNote,
  setSelectedNote,
  isEditingNote,
  setIsEditingNote,
  showDeleteNoteDialog,
  setShowDeleteNoteDialog,
  newNote,
  setNewNote,
  closeEntityOverview,
  fetchWstgChecklist,
  handleOpenLinkFinding,
  handleLinkFinding,
  handleUnlinkFinding,
  handleOpenCreateFinding,
  handleCreateNote,
  handleUpdateNote,
  handleDeleteNote,
  markUrlAsSeen,
  markAllUrlsAsSeen,
  refreshEntityOverview,
  markAsSeen,
  currentProject,
  currentUser,
  updateWstgItem
}) => {
  const [urlSortField, setUrlSortField] = useState(null);
  const [urlSortDirection, setUrlSortDirection] = useState('asc');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  
  // Ref to save scroll position for WSTG checklist
  const scrollPositionRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);
  const notesEditorRef = useRef(null);

  // Restore scroll position after WSTG checklist updates - use useLayoutEffect for synchronous restoration
  useLayoutEffect(() => {
    if (shouldRestoreScrollRef.current && sidePanelTab === 'wstg' && !wstgLoading && wstgChecklist) {
      const savedPosition = scrollPositionRef.current;
      console.log('useLayoutEffect - Attempting to restore scroll position:', savedPosition, 'wstgChecklist items:', wstgChecklist?.items?.length);
      
      const container = document.getElementById('wstg-items-container');
      if (container && savedPosition >= 0) {
        const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
        const targetPosition = Math.min(savedPosition, maxScroll);
        
        console.log('useLayoutEffect - scrollHeight:', container.scrollHeight, 'clientHeight:', container.clientHeight, 'maxScroll:', maxScroll, 'targetPosition:', targetPosition);
        
        // Set scroll position synchronously
        container.scrollTop = targetPosition;
        const actualPosition = container.scrollTop;
        console.log('useLayoutEffect - Scroll set. Saved:', savedPosition, 'Target:', targetPosition, 'Actual:', actualPosition);
        
        shouldRestoreScrollRef.current = false;
      }
    }
  }, [wstgChecklist, wstgLoading, sidePanelTab]);

  // Also use useEffect as fallback for cases where layout effect isn't enough
  useEffect(() => {
    if (shouldRestoreScrollRef.current && sidePanelTab === 'wstg' && !wstgLoading && wstgChecklist) {
      const savedPosition = scrollPositionRef.current;
      const container = document.getElementById('wstg-items-container');
      
      if (container && savedPosition >= 0) {
        // Double-check and restore if needed
        requestAnimationFrame(() => {
          const currentScroll = container.scrollTop;
          const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
          const targetPosition = Math.min(savedPosition, maxScroll);
          
          if (Math.abs(currentScroll - targetPosition) > 5) {
            console.log('useEffect fallback - Restoring scroll. Current:', currentScroll, 'Target:', targetPosition);
            container.scrollTop = targetPosition;
          }
          shouldRestoreScrollRef.current = false;
        });
      }
    }
  }, [wstgChecklist, wstgLoading, sidePanelTab]);

  // Wrapper function to save scroll position before opening link finding dialog
  const handleOpenLinkFindingWithScroll = async (item) => {
    const container = document.getElementById('wstg-items-container');
    const savedScrollPosition = container ? container.scrollTop : 0;
    
    // Always save position, even if it's 0
    if (container) {
      scrollPositionRef.current = savedScrollPosition;
      shouldRestoreScrollRef.current = true;
      console.log('Saved scroll position before opening dialog:', savedScrollPosition);
    }
    await handleOpenLinkFinding(item);
  };

  // Wrapper functions to save scroll position before linking/unlinking findings
  const handleLinkFindingWithScroll = async (findingId) => {
    const container = document.getElementById('wstg-items-container');
    const savedScrollPosition = container ? container.scrollTop : 0;
    
    // Always save position, even if it's 0
    if (container) {
      scrollPositionRef.current = savedScrollPosition;
      shouldRestoreScrollRef.current = true;
      console.log('Saved scroll position before linking:', savedScrollPosition);
    }
    await handleLinkFinding(findingId);
  };

  const handleUnlinkFindingWithScroll = async (item, findingId = null) => {
    const container = document.getElementById('wstg-items-container');
    const savedScrollPosition = container ? container.scrollTop : 0;
    
    // Always save position, even if it's 0
    if (container) {
      scrollPositionRef.current = savedScrollPosition;
      shouldRestoreScrollRef.current = true;
      console.log('Saved scroll position before unlinking:', savedScrollPosition);
    }
    await handleUnlinkFinding(item, findingId);
  };

  if (!selectedEntity) return null;

  // Helper to save scroll position - call this synchronously before any async operations
  const saveScrollPosition = () => {
    const container = document.getElementById('wstg-items-container');
    if (container) {
      const savedPosition = container.scrollTop;
      scrollPositionRef.current = savedPosition;
      shouldRestoreScrollRef.current = true;
      console.log('saveScrollPosition - Saved:', savedPosition, 'scrollHeight:', container.scrollHeight, 'clientHeight:', container.clientHeight);
      return savedPosition;
    }
    return 0;
  };

  const handleUpdateStatus = async (status, item) => {
    try {
      const updateData = {
        test_id: item.test_id,
        test_name: item.test_name,
        category: item.category,
        status: status,
        host_id: selectedEntity.type === 'host' ? selectedEntity.id : null,
        domain_id: selectedEntity.type === 'domain' ? selectedEntity.id : null,
      };
      
      await api.post(
        `/projects/${currentProject.id}/wstg-checklist`,
        updateData
      );
      
      // Optimistically update local state without full reload
      updateWstgItem(item.test_id, { status: status });
      
      toaster.success('Status updated');
    } catch (error) {
      console.error('Error updating status:', error);
      shouldRestoreScrollRef.current = false;
      toaster.danger('Failed to update status');
    }
  };

  const handleSaveNotes = async (item) => {
    try {
      const updateData = {
        test_id: item.test_id,
        test_name: item.test_name,
        category: item.category,
        notes: wstgItemNotes,
        host_id: selectedEntity.type === 'host' ? selectedEntity.id : null,
        domain_id: selectedEntity.type === 'domain' ? selectedEntity.id : null,
      };
      
      await api.post(
        `/projects/${currentProject.id}/wstg-checklist`,
        updateData
      );
      
      // Optimistically update local state without full reload
      updateWstgItem(item.test_id, { notes: wstgItemNotes });
      
      setEditingWstgItem(null);
      setWstgItemNotes('');
      
      toaster.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      shouldRestoreScrollRef.current = false;
      toaster.danger('Failed to save notes');
    }
  };

  return (
    <Pane
      width={600}
      height="100vh"
      position="fixed"
      right={0}
      top={0}
      background="white"
      borderLeft="1px solid #E6E8F0"
      display="flex"
      flexDirection="column"
      zIndex={1000}
      boxShadow="-2px 0 8px rgba(0,0,0,0.1)"
      transform={`translateX(${selectedEntity ? 0 : '100%'})`}
      transitionProperty="transform"
      transitionDuration="300ms"
      transitionTimingFunction="cubic-bezier(0.4, 0, 0.2, 1)"
      overflow="hidden"
    >
      {/* Header */}
      <Pane padding={16} borderBottom="1px solid #E6E8F0">
        <Pane display="flex" alignItems="center" marginBottom={12}>
          <Pane flex={1}>
            <Heading size={600} marginBottom={4}>
              {entityDetails?.name || entityDetails?.ip_address || entityDetails?.url || 'Overview'}
            </Heading>
            <Pane display="flex" alignItems="center" gap={8}>
              <Badge color="blue" marginRight={8}>
                {selectedEntity.type}
              </Badge>
              {entityDetails?.severity && (
                <Badge
                  backgroundColor={getSeverityBackgroundColor(entityDetails.severity)}
                  color={entityDetails.severity === 'critical' ? 'red' : 'default'}
                >
                  {entityDetails.severity}
                </Badge>
              )}
            </Pane>
          </Pane>
          <IconButton icon={CrossIcon} appearance="minimal" onClick={closeEntityOverview} />
        </Pane>
        
        {/* Tabs */}
        <Tablist>
          <Tab
            key="overview"
            id="overview"
            isSelected={sidePanelTab === 'overview'}
            onSelect={() => setSidePanelTab('overview')}
          >
            Overview
          </Tab>
          <Tab
            key="notes"
            id="notes"
            isSelected={sidePanelTab === 'notes'}
            onSelect={() => setSidePanelTab('notes')}
          >
            Notes {notes.length > 0 && `(${notes.length})`}
          </Tab>
          <Tab
            key="findings"
            id="findings"
            isSelected={sidePanelTab === 'findings'}
            onSelect={() => setSidePanelTab('findings')}
          >
            Findings {entityOverview?.findings?.length > 0 && `(${entityOverview.findings.length})`}
          </Tab>
          <Tab
            key="urls"
            id="urls"
            isSelected={sidePanelTab === 'urls'}
            onSelect={() => setSidePanelTab('urls')}
          >
            URLs {entityOverview?.urls?.length > 0 && `(${entityOverview.urls.length})`}
          </Tab>
          <Tab
            key="screenshots"
            id="screenshots"
            isSelected={sidePanelTab === 'screenshots'}
            onSelect={() => setSidePanelTab('screenshots')}
          >
            Screenshots {entityOverview?.screenshots?.length > 0 && `(${entityOverview.screenshots.length})`}
          </Tab>
          {(selectedEntity.type === 'host' || selectedEntity.type === 'domain') && (
            <Tab
              key="wstg"
              id="wstg"
              isSelected={sidePanelTab === 'wstg'}
              onSelect={() => setSidePanelTab('wstg')}
            >
              OWASP WSTG
            </Tab>
          )}
        </Tablist>
      </Pane>

      {/* Content */}
      {overviewLoading ? (
        <Pane display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Spinner />
        </Pane>
      ) : entityOverview ? (
        <Pane flex={1} overflowY="auto" padding={16}>
          {/* Overview Tab */}
          {sidePanelTab === 'overview' && (
            <>
              {/* Host Overview */}
              {selectedEntity.type === 'host' && entityDetails?.ip_address && (
                <Pane>
                  <Heading size={500} marginBottom={16}>
                    {entityDetails.ip_address}
                  </Heading>
                  {entityOverview.host && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={8}>Host Information</Heading>
                      <Pane marginBottom={8}>
                        <Text size={300} color="muted">Sources:</Text>
                        <Pane display="flex" flexWrap="wrap" gap={4} marginTop={4}>
                          {(entityOverview.host.sources || []).map((source, idx) => (
                            <Badge key={idx} backgroundColor="#E6F4EA" color="default">
                              {source}
                            </Badge>
                          ))}
                          {(!entityOverview.host.sources || entityOverview.host.sources.length === 0) && (
                            <Text size={300} color="muted">N/A</Text>
                          )}
                        </Pane>
                      </Pane>
                    </Card>
                  )}
                  
                  {/* WSTG Checklist Progress */}
                  {wstgChecklist && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" justifyContent="space-between" alignItems="center">
                        <Pane>
                          <Heading size={400} marginBottom={4}>OWASP WSTG Checklist</Heading>
                          <Text size={300} color="muted">
                            {wstgChecklist.completed_tests} of {wstgChecklist.total_tests} tests completed
                          </Text>
                        </Pane>
                        <Pane>
                          <Heading size={600} color="blue">
                            {wstgChecklist.completion_percentage.toFixed(1)}%
                          </Heading>
                        </Pane>
                      </Pane>
                      <Button 
                        marginTop={12} 
                        appearance="primary" 
                        onClick={() => setSidePanelTab('wstg')}
                      >
                        View Checklist →
                      </Button>
                    </Card>
                  )}
                  
                  {entityOverview.domains && entityOverview.domains.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={12}>Associated Domains ({entityOverview.domains.length})</Heading>
                      <Pane display="flex" flexWrap="wrap" gap={4}>
                        {entityOverview.domains.map(domain => (
                          <Badge key={domain.id} backgroundColor="#F7F8FA" color="default">
                            {domain.name}
                          </Badge>
                        ))}
                      </Pane>
                    </Card>
                  )}
                  
                  {entityOverview.ports && entityOverview.ports.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={12}>Open Ports ({entityOverview.ports.length})</Heading>
                      <Pane display="flex" flexDirection="column" gap={4}>
                        {entityOverview.ports.map(port => (
                          <Card key={port.id} padding={12} background="white">
                            <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={4}>
                              <Strong>{port.number}/{port.protocol}</Strong>
                              <Badge backgroundColor={port.state === 'open' ? '#E6F4EA' : '#F7F8FA'}>
                                {port.state}
                              </Badge>
                            </Pane>
                            {port.service && (
                              <Text size={300} color="muted">Service: {port.service}</Text>
                            )}
                            {port.banner && (
                              <Text size={300} color="muted" marginTop={4} style={{ fontFamily: 'monospace' }}>
                                Banner: {port.banner.substring(0, 100)}
                              </Text>
                            )}
                          </Card>
                        ))}
                      </Pane>
                    </Card>
                  )}
                  
                  {entityOverview.urls && entityOverview.urls.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>URLs ({entityOverview.urls.length})</Heading>
                        <Button size="small" appearance="minimal" onClick={() => setSidePanelTab('urls')}>
                          View All →
                        </Button>
                      </Pane>
                      <Text size={300} color="muted">
                        Click "View All" to see all URLs in a table format
                      </Text>
                    </Card>
                  )}
                  
                  {entityOverview.findings && entityOverview.findings.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>Findings ({entityOverview.findings.length})</Heading>
                        <Button size="small" appearance="minimal" onClick={() => setSidePanelTab('findings')}>
                          View All →
                        </Button>
                      </Pane>
                      <Pane display="flex" flexDirection="column" gap={4}>
                        {entityOverview.findings.slice(0, 3).map(finding => (
                          <Card key={finding.id} padding={12} background="white">
                            <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={4}>
                              <Strong size={300}>{finding.name}</Strong>
                              <Badge
                                backgroundColor={getSeverityBackgroundColor(finding.severity)}
                                color={finding.severity === 'critical' ? 'red' : 'default'}
                              >
                                {finding.severity}
                              </Badge>
                            </Pane>
                            {finding.description && (
                              <Text size={300} color="muted">
                                {finding.description.length > 150 
                                  ? `${finding.description.substring(0, 150)}...` 
                                  : finding.description}
                              </Text>
                            )}
                          </Card>
                        ))}
                        {entityOverview.findings.length > 3 && (
                          <Text size={300} color="muted" marginTop={8}>
                            +{entityOverview.findings.length - 3} more findings. Switch to Findings tab to see all.
                          </Text>
                        )}
                      </Pane>
                    </Card>
                  )}
                  
                  {entityOverview.notes && entityOverview.notes.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>Notes ({entityOverview.notes.length})</Heading>
                        <Button size="small" appearance="minimal" onClick={() => setSidePanelTab('notes')}>
                          View All →
                        </Button>
                      </Pane>
                      <Pane display="flex" flexDirection="column" gap={4}>
                        {entityOverview.notes.slice(0, 2).map(note => (
                          <Card key={note.id} padding={12} background="white">
                            <Heading size={300} marginBottom={4}>{note.title || 'Untitled Note'}</Heading>
                            <Text size={300} color="muted">
                              {note.content.length > 100 
                                ? `${note.content.substring(0, 100)}...` 
                                : note.content}
                            </Text>
                          </Card>
                        ))}
                        {entityOverview.notes.length > 2 && (
                          <Text size={300} color="muted" marginTop={8}>
                            +{entityOverview.notes.length - 2} more notes. Switch to Notes tab to see all.
                          </Text>
                        )}
                      </Pane>
                    </Card>
                  )}
                </Pane>
              )}
              
              {/* Domain Overview */}
              {selectedEntity.type === 'domain' && entityDetails?.name && (
                <Pane>
                  <Heading size={500} marginBottom={16}>
                    {entityDetails.name}
                  </Heading>
                  {entityOverview.domain && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={8}>Domain Information</Heading>
                      <Pane marginBottom={8}>
                        <Text size={300} color="muted">Sources:</Text>
                        <Pane display="flex" flexWrap="wrap" gap={4} marginTop={4}>
                          {(entityOverview.domain.sources || []).map((source, idx) => (
                            <Badge key={idx} backgroundColor="#E6F4EA" color="default">
                              {source}
                            </Badge>
                          ))}
                          {(!entityOverview.domain.sources || entityOverview.domain.sources.length === 0) && (
                            <Text size={300} color="muted">N/A</Text>
                          )}
                        </Pane>
                      </Pane>
                    </Card>
                  )}
                  
                  {/* WSTG Checklist Progress */}
                  {wstgChecklist && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" justifyContent="space-between" alignItems="center">
                        <Pane>
                          <Heading size={400} marginBottom={4}>OWASP WSTG Checklist</Heading>
                          <Text size={300} color="muted">
                            {wstgChecklist.completed_tests} of {wstgChecklist.total_tests} tests completed
                          </Text>
                        </Pane>
                        <Pane>
                          <Heading size={600} color="blue">
                            {wstgChecklist.completion_percentage.toFixed(1)}%
                          </Heading>
                        </Pane>
                      </Pane>
                      <Button 
                        marginTop={12} 
                        appearance="primary" 
                        onClick={() => setSidePanelTab('wstg')}
                      >
                        View Checklist →
                      </Button>
                    </Card>
                  )}
                  
                  {entityOverview.hosts && entityOverview.hosts.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={12}>Associated Hosts ({entityOverview.hosts.length})</Heading>
                      <Pane display="flex" flexWrap="wrap" gap={4}>
                        {entityOverview.hosts.map(host => (
                          <Badge key={host.id} backgroundColor="#F7F8FA" color="default">
                            {host.ip_address}
                          </Badge>
                        ))}
                      </Pane>
                    </Card>
                  )}
                  
                  {entityOverview.ports && entityOverview.ports.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={12}>Ports ({entityOverview.ports.length})</Heading>
                      <Pane display="flex" flexDirection="column" gap={4}>
                        {entityOverview.ports.map(port => (
                          <Card key={port.id} padding={12} background="white">
                            <Pane display="flex" alignItems="center" justifyContent="space-between">
                              <Strong>{port.number}/{port.protocol}</Strong>
                              <Badge backgroundColor={port.state === 'open' ? '#E6F4EA' : '#F7F8FA'}>
                                {port.state}
                              </Badge>
                            </Pane>
                            {port.service && (
                              <Text size={300} color="muted">Service: {port.service}</Text>
                            )}
                          </Card>
                        ))}
                      </Pane>
                    </Card>
                  )}
                  
                  {entityOverview.urls && entityOverview.urls.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>URLs ({entityOverview.urls.length})</Heading>
                        <Button size="small" appearance="minimal" onClick={() => setSidePanelTab('urls')}>
                          View All →
                        </Button>
                      </Pane>
                      <Text size={300} color="muted">
                        Click "View All" to see all URLs in a table format
                      </Text>
                    </Card>
                  )}
                  
                  {entityOverview.findings && entityOverview.findings.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>Findings ({entityOverview.findings.length})</Heading>
                        <Button size="small" appearance="minimal" onClick={() => setSidePanelTab('findings')}>
                          View All →
                        </Button>
                      </Pane>
                      <Pane display="flex" flexDirection="column" gap={4}>
                        {entityOverview.findings.slice(0, 3).map(finding => (
                          <Card key={finding.id} padding={12} background="white">
                            <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={4}>
                              <Strong size={300}>{finding.name}</Strong>
                              <Badge
                                backgroundColor={getSeverityBackgroundColor(finding.severity)}
                                color={finding.severity === 'critical' ? 'red' : 'default'}
                              >
                                {finding.severity}
                              </Badge>
                            </Pane>
                            {finding.description && (
                              <Text size={300} color="muted">
                                {finding.description.length > 150 
                                  ? `${finding.description.substring(0, 150)}...` 
                                  : finding.description}
                              </Text>
                            )}
                          </Card>
                        ))}
                        {entityOverview.findings.length > 3 && (
                          <Text size={300} color="muted" marginTop={8}>
                            +{entityOverview.findings.length - 3} more findings. Switch to Findings tab to see all.
                          </Text>
                        )}
                      </Pane>
                    </Card>
                  )}
                  
                  {entityOverview.notes && entityOverview.notes.length > 0 && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>Notes ({entityOverview.notes.length})</Heading>
                        <Button size="small" appearance="minimal" onClick={() => setSidePanelTab('notes')}>
                          View All →
                        </Button>
                      </Pane>
                      <Pane display="flex" flexDirection="column" gap={4}>
                        {entityOverview.notes.slice(0, 2).map(note => (
                          <Card key={note.id} padding={12} background="white">
                            <Heading size={300} marginBottom={4}>{note.title || 'Untitled Note'}</Heading>
                            <Text size={300} color="muted">
                              {note.content.length > 100 
                                ? `${note.content.substring(0, 100)}...` 
                                : note.content}
                            </Text>
                          </Card>
                        ))}
                        {entityOverview.notes.length > 2 && (
                          <Text size={300} color="muted" marginTop={8}>
                            +{entityOverview.notes.length - 2} more notes. Switch to Notes tab to see all.
                          </Text>
                        )}
                      </Pane>
                    </Card>
                  )}
                </Pane>
              )}
              
              {/* URL/Webapp Overview */}
              {(selectedEntity.type === 'url' || selectedEntity.type === 'webapp') && entityDetails?.url && (
                <Pane>
                  <Heading size={500} marginBottom={16}>
                    {entityDetails.url}
                  </Heading>
                  {entityOverview?.url?.host && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={8}>Host</Heading>
                      <Text>{entityOverview.url.host.ip_address}</Text>
                    </Card>
                  )}
                  {entityOverview?.url?.domain && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={8}>Domain</Heading>
                      <Text>{entityOverview.url.domain.name}</Text>
                    </Card>
                  )}
                  {entityOverview?.url?.screenshot && (
                    <Card padding={16} marginBottom={16} background="tint1">
                      <Heading size={400} marginBottom={8}>Screenshot</Heading>
                      <img
                        src={entityOverview.url.screenshot.thumbnail_url}
                        alt="Screenshot"
                        style={{ maxWidth: '100%', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => setSelectedScreenshot(entityOverview.url.screenshot)}
                      />
                    </Card>
                  )}
                </Pane>
              )}
            </>
          )}
          
          {/* Notes Tab */}
          {sidePanelTab === 'notes' && (
            <Pane>
              <Pane marginBottom={16} display="flex" justifyContent="space-between" alignItems="center">
                <Heading size={500}>Notes ({notes.length})</Heading>
                <Button
                  appearance="primary"
                  onClick={() => {
                    setSelectedNote(null);
                    setIsEditingNote(true);
                    setNewNote({ title: '', content: '' });
                  }}
                >
                  Create Note
                </Button>
              </Pane>
              
              {isEditingNote && (
                <Card padding={16} marginBottom={16} background="tint1">
                  <TextInput
                    placeholder="Note Title"
                    value={selectedNote?.title || newNote.title}
                    onChange={e => 
                      selectedNote
                        ? setSelectedNote({ ...selectedNote, title: e.target.value })
                        : setNewNote({ ...newNote, title: e.target.value })
                    }
                    marginBottom={12}
                  />
                  <Pane ref={notesEditorRef}>
                    <MDEditor
                      value={selectedNote?.content || newNote.content}
                      onChange={value => 
                        selectedNote
                          ? setSelectedNote({ ...selectedNote, content: value || '' })
                          : setNewNote({ ...newNote, content: value || '' })
                      }
                      height={300}
                      preview="edit"
                      data-color-mode="light"
                      onPaste={async (event) => {
                        const items = event.clipboardData?.items;
                        if (!items || !currentProject?.id) return;

                        const imageFiles = [];
                        for (let i = 0; i < items.length; i++) {
                          const item = items[i];
                          if (item.type && item.type.indexOf('image') !== -1) {
                            const file = item.getAsFile();
                            if (file) {
                              imageFiles.push(file);
                            }
                          }
                        }

                        if (imageFiles.length > 0) {
                          event.preventDefault();
                          event.stopPropagation();
                          setUploadingImage(true);
                          
                          // Get current content and cursor position
                          const currentContent = selectedNote?.content || newNote.content || '';
                          const textarea = event.target;
                          let cursorStart = currentContent.length;
                          let cursorEnd = currentContent.length;
                          
                          if (textarea && (textarea.tagName === 'TEXTAREA' || textarea.tagName === 'INPUT')) {
                            cursorStart = textarea.selectionStart || currentContent.length;
                            cursorEnd = textarea.selectionEnd || currentContent.length;
                          }
                          
                          try {
                            for (const file of imageFiles) {
                              if (file.size > 10 * 1024 * 1024) {
                                toaster.danger(`Image ${file.name || 'image'} is too large (max 10MB)`);
                                continue;
                              }

                              const formData = new FormData();
                              formData.append('file', file);
                              
                              const response = await api.post(
                                `/projects/${currentProject.id}/markdown-images`,
                                formData,
                                {
                                  headers: {
                                    'Content-Type': 'multipart/form-data',
                                  },
                                }
                              );

                              const imageUrl = response.data.url;
                              const fileName = file.name || 'image';
                              const imageMarkdown = `![${fileName}](${imageUrl})\n`;
                              
                              const before = currentContent.substring(0, cursorStart);
                              const after = currentContent.substring(cursorEnd);
                              const newContent = before + imageMarkdown + after;
                              
                              if (selectedNote) {
                                setSelectedNote({ ...selectedNote, content: newContent });
                              } else {
                                setNewNote({ ...newNote, content: newContent });
                              }
                              
                              // Update cursor position
                              setTimeout(() => {
                                const editorContainer = notesEditorRef.current;
                                if (editorContainer) {
                                  const textarea = editorContainer.querySelector('textarea');
                                  if (textarea) {
                                    const newPos = cursorStart + imageMarkdown.length;
                                    textarea.setSelectionRange(newPos, newPos);
                                    textarea.focus();
                                  }
                                }
                              }, 0);
                              
                              cursorStart += imageMarkdown.length;
                              cursorEnd = cursorStart;
                              
                              toaster.success('Image uploaded and inserted');
                            }
                          } catch (error) {
                            console.error('Error uploading image:', error);
                            toaster.danger('Failed to upload image');
                          } finally {
                            setUploadingImage(false);
                          }
                        }
                      }}
                    />
                  </Pane>
                  {uploadingImage && (
                    <Pane marginTop={8} display="flex" alignItems="center" gap={8}>
                      <Spinner size={16} />
                      <Text size={300} color="muted">Uploading image...</Text>
                    </Pane>
                  )}
                  <Pane marginTop={12} display="flex" justifyContent="flex-end" gap={8}>
                    <Button
                      appearance="minimal"
                      onClick={() => {
                        setIsEditingNote(false);
                        setSelectedNote(null);
                        setNewNote({ title: '', content: '' });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      appearance="primary"
                      onClick={selectedNote ? handleUpdateNote : handleCreateNote}
                    >
                      {selectedNote ? 'Update' : 'Create'} Note
                    </Button>
                  </Pane>
                </Card>
              )}
              
              {notesLoading ? (
                <Pane display="flex" justifyContent="center" padding={32}>
                  <Spinner />
                </Pane>
              ) : notes.length === 0 ? (
                <Pane padding={32} textAlign="center">
                  <Text color="muted">No notes yet. Create your first note!</Text>
                </Pane>
              ) : (
                <Pane display="flex" flexDirection="column" gap={12}>
                  {notes.map(note => (
                    <Card key={note.id} padding={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={8}>
                        <Heading size={400}>{note.title || 'Untitled Note'}</Heading>
                        <Pane>
                          <IconButton
                            icon={EditIcon}
                            appearance="minimal"
                            marginRight={8}
                            onClick={() => {
                              setSelectedNote(note);
                              setIsEditingNote(true);
                            }}
                          />
                          <IconButton
                            icon={TrashIcon}
                            appearance="minimal"
                            intent="danger"
                            onClick={() => {
                              setSelectedNote(note);
                              setShowDeleteNoteDialog(true);
                            }}
                          />
                        </Pane>
                      </Pane>
                      <Pane backgroundColor="white" padding={12} borderRadius={4} marginBottom={8}>
                        <MDEditor.Markdown 
                          source={note.content} 
                          data-color-mode="light"
                          style={{ backgroundColor: 'white', color: 'black' }}
                        />
                      </Pane>
                    </Card>
                  ))}
                </Pane>
              )}
            </Pane>
          )}
          
          {/* Findings Tab */}
          {sidePanelTab === 'findings' && (
            <Pane>
              <Heading size={500} marginBottom={16}>
                Findings ({entityOverview?.findings?.length || 0})
              </Heading>
              {entityOverview?.findings && entityOverview.findings.length > 0 ? (
                <Pane display="flex" flexDirection="column" gap={12}>
                  {entityOverview.findings.map(finding => (
                    <Card key={finding.id} padding={16} background="tint1">
                      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
                        <Heading size={400}>{finding.name}</Heading>
                        <Badge
                          backgroundColor={getSeverityBackgroundColor(finding.severity)}
                          color={finding.severity === 'critical' ? 'red' : 'default'}
                        >
                          {finding.severity}
                        </Badge>
                      </Pane>
                      {finding.description && (
                        <Pane marginBottom={12}>
                          <Text>{finding.description}</Text>
                        </Pane>
                      )}
                      {finding.url && (
                        <Pane marginBottom={8}>
                          <Text size={300} color="muted" marginBottom={4}>URL:</Text>
                          <Text color="blue500">
                            <a href={finding.url} target="_blank" rel="noopener noreferrer">
                              {finding.url}
                            </a>
                          </Text>
                        </Pane>
                      )}
                      {finding.source && (
                        <Badge backgroundColor="#F7F8FA" marginTop={8}>
                          Source: {finding.source}
                        </Badge>
                      )}
                      {finding.status && (
                        <Badge backgroundColor="#F7F8FA" marginTop={8} marginLeft={8}>
                          Status: {finding.status}
                        </Badge>
                      )}
                    </Card>
                  ))}
                </Pane>
              ) : (
                <Pane padding={32} textAlign="center">
                  <Text color="muted">No findings associated with this entity</Text>
                </Pane>
              )}
            </Pane>
          )}
          
          {/* URLs Tab */}
          {sidePanelTab === 'urls' && (() => {
            const sortedUrls = [...(entityOverview?.urls || [])].sort((a, b) => {
              if (!urlSortField) return 0;
              
              let aVal, bVal;
              if (urlSortField === 'http_code') {
                aVal = a.http_code || 0;
                bVal = b.http_code || 0;
              } else if (urlSortField === 'size') {
                aVal = a.content_length || 0;
                bVal = b.content_length || 0;
              } else {
                return 0;
              }
              
              if (urlSortDirection === 'asc') {
                return aVal - bVal;
              } else {
                return bVal - aVal;
              }
            });
            
            const handleSort = (field) => {
              if (urlSortField === field) {
                setUrlSortDirection(urlSortDirection === 'asc' ? 'desc' : 'asc');
              } else {
                setUrlSortField(field);
                setUrlSortDirection('asc');
              }
            };

            const handleMarkUrlAsSeen = async (urlId) => {
              if (!currentUser?.id) {
                toaster.danger('User not found');
                return;
              }
              
              // Update local state immediately for instant feedback
              if (setEntityOverview && entityOverview?.urls) {
                setEntityOverview({
                  ...entityOverview,
                  urls: entityOverview.urls.map(url => 
                    url.id === urlId 
                      ? { ...url, seen_by_user_id: currentUser.id }
                      : url
                  )
                });
              }
              
              try {
                await markUrlAsSeen(urlId);
                // Don't refresh immediately - let the local state update be the source of truth
                // The server state will be synced on next manual refresh or panel reopen
              } catch (error) {
                console.error('Error marking URL as seen:', error);
                // If API call fails, revert local state
                if (setEntityOverview && entityOverview?.urls) {
                  await refreshEntityOverview();
                }
              }
            };

            const handleMarkAllAsSeen = async () => {
              if (!currentUser?.id) {
                toaster.danger('User not found');
                return;
              }
              
              // Update local state immediately for instant feedback
              if (setEntityOverview && entityOverview?.urls) {
                const unseenUrls = entityOverview.urls.filter(
                  url => !url.seen_by_user_id || url.seen_by_user_id !== currentUser.id
                );
                
                if (unseenUrls.length > 0) {
                  const markedUrlIds = new Set(unseenUrls.map(url => url.id));
                  
                  setEntityOverview({
                    ...entityOverview,
                    urls: entityOverview.urls.map(url => 
                      markedUrlIds.has(url.id)
                        ? { ...url, seen_by_user_id: currentUser.id }
                        : url
                    )
                  });
                }
              }
              
              // Pass currentUser from props to markAllUrlsAsSeen
              const result = await markAllUrlsAsSeen(entityOverview?.urls || [], currentUser);
              
              // Don't refresh immediately - let the local state update be the source of truth
              // The server state will be synced on next manual refresh or panel reopen
              if (result && !result.success) {
                // If API call fails, revert local state
                await refreshEntityOverview();
              }
            };
            
            return (
              <Pane>
                <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
                  <Heading size={500}>
                    URLs ({entityOverview?.urls?.length || 0})
                  </Heading>
                  {entityOverview?.urls && entityOverview.urls.length > 0 && (
                    <Button
                      size="small"
                      appearance="default"
                      onClick={handleMarkAllAsSeen}
                    >
                      Mark All as Seen
                    </Button>
                  )}
                </Pane>
                {entityOverview?.urls && entityOverview.urls.length > 0 ? (
                  <Pane border="1px solid #E6E8F0" borderRadius={4} overflow="hidden">
                    {/* Table Header */}
                    <Pane 
                      display="flex" 
                      padding={12} 
                      background="#F7F8FA" 
                      borderBottom="1px solid #E6E8F0"
                    >
                      <Pane width="60%"><Text size={400} fontWeight={600}>URL</Text></Pane>
                      <Pane 
                        width="15%" 
                        display="flex" 
                        justifyContent="center" 
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => handleSort('http_code')}
                      >
                        <Text size={400} fontWeight={600}>
                          HTTP
                          {urlSortField === 'http_code' && (
                            <Text display="inline" marginLeft={4}>
                              {urlSortDirection === 'asc' ? '↑' : '↓'}
                            </Text>
                          )}
                        </Text>
                      </Pane>
                      <Pane 
                        width="15%" 
                        display="flex" 
                        justifyContent="center" 
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => handleSort('size')}
                      >
                        <Text size={400} fontWeight={600}>
                          Size
                          {urlSortField === 'size' && (
                            <Text display="inline" marginLeft={4}>
                              {urlSortDirection === 'asc' ? '↑' : '↓'}
                            </Text>
                          )}
                        </Text>
                      </Pane>
                      <Pane width="10%" display="flex" justifyContent="center"><Text size={400} fontWeight={600}>Action</Text></Pane>
                    </Pane>
                    
                    {/* Table Rows */}
                    <Pane maxHeight="600px" overflowY="auto">
                      {sortedUrls.map(url => {
                        const isNew = !url.seen_by_user_id || url.seen_by_user_id !== currentUser.id;
                        const httpCode = url.http_code;
                        const isSuccess = httpCode === 200;
                        const isError = httpCode && [400, 403, 404, 500].includes(httpCode);
                        
                        return (
                          <Pane
                            key={url.id}
                            display="flex"
                            padding={12}
                            borderBottom="1px solid #E6E8F0"
                            background={isNew ? "#E6F4EA" : "white"}
                            borderLeft={isNew ? "3px solid #47B881" : "none"}
                          >
                            <Pane width="60%" display="flex" alignItems="center" paddingRight={8}>
                              <Text 
                                size={400} 
                                color={isNew ? "green700" : "blue500"}
                                style={{ 
                                  cursor: 'pointer',
                                  wordBreak: 'break-all'
                                }}
                                onClick={() => {
                                  window.open(url.url, '_blank');
                                  if (isNew) {
                                    handleMarkUrlAsSeen(url.id);
                                  }
                                }}
                              >
                                {url.url}
                              </Text>
                            </Pane>
                            <Pane width="15%" display="flex" alignItems="center" justifyContent="center">
                              {httpCode ? (
                                <Text 
                                  size={400} 
                                  fontWeight={600}
                                  color={isSuccess ? '#47B881' : (isError ? '#D14343' : 'default')}
                                >
                                  {httpCode}
                                </Text>
                              ) : (
                                <Text size={300} color="muted">-</Text>
                              )}
                            </Pane>
                            <Pane width="15%" display="flex" alignItems="center" justifyContent="center">
                              {url.content_length ? (
                                <Text size={400}>
                                  {(url.content_length / 1024).toFixed(1)} KB
                                </Text>
                              ) : (
                                <Text size={300} color="muted">-</Text>
                              )}
                            </Pane>
                            <Pane width="10%" display="flex" alignItems="center" justifyContent="center" gap={4}>
                              {isNew && (
                                <IconButton
                                  icon={TickIcon}
                                  size="small"
                                  appearance="minimal"
                                  intent="success"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkUrlAsSeen(url.id);
                                  }}
                                  title="Mark as seen"
                                />
                              )}
                              <IconButton
                                icon={TrashIcon}
                                size="small"
                                appearance="minimal"
                                intent="danger"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Delete this URL?')) {
                                    try {
                                      await api.delete(`/projects/${currentProject.id}/results/urls/${url.id}`);
                                      toaster.success('URL deleted');
                                      refreshEntityOverview();
                                    } catch (err) {
                                      toaster.danger('Failed to delete URL');
                                    }
                                  }
                                }}
                                title="Delete URL"
                              />
                            </Pane>
                          </Pane>
                        );
                      })}
                    </Pane>
                  </Pane>
                ) : (
                  <Pane padding={32} textAlign="center">
                    <Text color="muted">No URLs associated with this entity</Text>
                  </Pane>
                )}
              </Pane>
            );
          })()}
          
          {/* Screenshots Tab */}
          {sidePanelTab === 'screenshots' && (
            <Pane>
              <Heading size={500} marginBottom={16}>
                Screenshots ({entityOverview?.screenshots?.length || 0})
              </Heading>
              {entityOverview?.screenshots && entityOverview.screenshots.length > 0 ? (
                <Pane display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={12}>
                  {entityOverview.screenshots.map(screenshot => (
                    <Card key={screenshot.id} padding={8} background="tint1" position="relative">
                      <img
                        src={screenshot.thumbnail_url || screenshot.screenshot_url}
                        alt="Screenshot"
                        style={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: '4px',
                          border: '1px solid #E6E8F0',
                          cursor: 'pointer',
                          objectFit: 'contain'
                        }}
                        onClick={() => setSelectedScreenshot(screenshot)}
                      />
                      <Pane display="flex" justifyContent="space-between" alignItems="center" marginTop={4}>
                        {screenshot.url ? (
                          <Text size={300} color="muted" isTruncated flex={1}>
                            {screenshot.url}
                          </Text>
                        ) : <Pane flex={1} />}
                        <IconButton
                          icon={TrashIcon}
                          intent="danger"
                          size="small"
                          appearance="minimal"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this screenshot?')) {
                              try {
                                await api.delete(`/projects/${currentProject.id}/screenshots/${screenshot.id}`);
                                toaster.success('Screenshot deleted');
                                refreshEntityOverview();
                              } catch (err) {
                                toaster.danger('Failed to delete screenshot');
                              }
                            }
                          }}
                        />
                      </Pane>
                    </Card>
                  ))}
                </Pane>
              ) : (
                <Pane padding={32} textAlign="center">
                  <Text color="muted">No screenshots associated with this entity</Text>
                </Pane>
              )}
            </Pane>
          )}
          
          {/* WSTG Checklist Tab */}
          {sidePanelTab === 'wstg' && (
            <Pane display="flex" flexDirection="column" flex={1} overflow="hidden">
              <Heading size={500} marginBottom={16}>
                OWASP WSTG Checklist
              </Heading>
              {wstgLoading ? (
                <Pane display="flex" justifyContent="center" alignItems="center" padding={32}>
                  <Spinner />
                </Pane>
              ) : wstgChecklist ? (
                <>
                  {/* Completion Percentage */}
                  <Card padding={16} marginBottom={16} background="tint1">
                    <Pane display="flex" justifyContent="space-between" alignItems="center">
                      <Pane>
                        <Heading size={400} marginBottom={4}>Completion</Heading>
                        <Text size={300} color="muted">
                          {wstgChecklist.completed_tests} of {wstgChecklist.total_tests} tests completed
                        </Text>
                      </Pane>
                      <Pane>
                        <Heading size={600} color="blue">
                          {wstgChecklist.completion_percentage.toFixed(1)}%
                        </Heading>
                      </Pane>
                    </Pane>
                  </Card>
                  
                  {/* Checklist Table */}
                  <Pane border="1px solid #E5E7EB" borderRadius={4} overflow="hidden" display="flex" flexDirection="column" flex={1} minHeight={0}>
                    {/* Table Header */}
                    <Pane 
                      display="flex" 
                      padding={12} 
                      background="#F9FAFB" 
                      borderBottom="1px solid #E5E7EB"
                      flexShrink={0}
                    >
                      <Pane width="20%"><Text size={300} fontWeight={600}>Test Name</Text></Pane>
                      <Pane width="60%"><Text size={400} fontWeight={600}>Category</Text></Pane>
                      <Pane width="20%"><Text size={400} fontWeight={600}>Actions</Text></Pane>
                    </Pane>
                    
                    {/* Table Rows */}
                    <Pane flex={1} overflowY="auto" minHeight={0} id="wstg-items-container">
                      {wstgChecklist.items.map((item, idx) => {
                        const isCompleted = item.status !== null && item.status !== undefined;
                        const isEditing = editingWstgItem === item.test_id;
                        
                        return (
                          <Pane
                            key={item.test_id || idx}
                            padding={12}
                            borderBottom="1px solid #E5E7EB"
                            background={isCompleted ? (item.status === 'Vulnerability found' ? "#FEF2F2" : "#F0FDF4") : "white"}
                          >
                            <Pane display="flex" alignItems="flex-start" gap={8}>
                              <Pane width="20%" display="flex" alignItems="center" paddingRight={8}>
                                <Text 
                                  size={300} 
                                  color={isCompleted ? (item.status === 'Vulnerability found' ? "#B91C1C" : "#047857") : "#6B7280"}
                                  style={{ fontWeight: 400, fontSize: '12px' }}
                                >
                                  {item.test_name}
                                </Text>
                              </Pane>
                              <Pane width="20%" display="flex" alignItems="center">
                                <Text 
                                  size={300} 
                                  color={isCompleted ? (item.status === 'Vulnerability found' ? "#991B1B" : "#065F46") : "#6B7280"}
                                  style={{ fontWeight: 400 }}
                                >
                                  {item.category}
                                </Text>
                              </Pane>
                              <Pane width="60%" display="flex" alignItems="flex-start" justifyContent="space-between" gap={8}>
                                {/* Left side: Status and Notes */}
                                <Pane display="flex" flexDirection="column" gap={8} flex={1}>
                                  {/* Status Badge */}
                                  <Pane display="flex" alignItems="center" gap={8} flexWrap="wrap">
                                    {item.status ? (
                                      <Badge 
                                        backgroundColor={item.status === 'OK.' ? '#10B981' : (item.status === 'Vulnerability found' ? '#EF4444' : '#10B981')} 
                                        color="white"
                                        style={{ fontWeight: 500 }}
                                      >
                                        {item.status}
                                      </Badge>
                                    ) : (
                                      <Text size={300} color="#6B7280" style={{ fontWeight: 400 }}>Not tested</Text>
                                    )}
                                    
                                    {/* Linked Findings Cards - Support multiple findings - Compact version */}
                                    {((item.findings && item.findings.length > 0) || (item.finding && !item.findings)) && (
                                      <Pane marginTop={2} display="flex" flexDirection="column" gap={2}>
                                        {/* Show multiple findings from new relationship */}
                                        {item.findings && item.findings.length > 0 && item.findings.map((finding, fIdx) => (
                                          <Pane
                                            key={finding.id || fIdx}
                                            backgroundColor="#F3F4F6"
                                            border="1px solid #D1D5DB"
                                            padding={3}
                                            borderRadius={3}
                                            width="100%"
                                            position="relative"
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                          >
                                            <Pane flex={1} paddingRight={2} display="flex" alignItems="center" gap={4}>
                                              <Text size={200} fontWeight={500} color="#111827" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                                                {finding.name || 'Linked Finding'}
                                              </Text>
                                              {finding.severity && (
                                                <Badge
                                                  backgroundColor={getSeverityBackgroundColor(finding.severity)}
                                                  color={getSeverityTextColor(finding.severity)}
                                                  style={{ 
                                                    fontWeight: 600, 
                                                    fontSize: '8px',
                                                    padding: '1px 4px',
                                                    border: `1px solid ${getSeverityTextColor(finding.severity)}`,
                                                    display: 'inline-block',
                                                    lineHeight: '1.2'
                                                  }}
                                                >
                                                  {finding.severity.toUpperCase()}
                                                </Badge>
                                              )}
                                            </Pane>
                                            <IconButton
                                              icon={CrossIcon}
                                              size={10}
                                              appearance="minimal"
                                              height={14}
                                              width={14}
                                              padding={0}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleUnlinkFinding(item, finding.id);
                                              }}
                                              title="Unlink finding"
                                            />
                                          </Pane>
                                        ))}
                                        {/* Show old single finding for backward compatibility */}
                                        {item.finding && (!item.findings || item.findings.length === 0) && (
                                          <Pane
                                            backgroundColor="#F3F4F6"
                                            border="1px solid #D1D5DB"
                                            padding={3}
                                            borderRadius={3}
                                            width="100%"
                                            position="relative"
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                          >
                                            <Pane flex={1} paddingRight={2} display="flex" alignItems="center" gap={4}>
                                              <Text size={200} fontWeight={500} color="#111827" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                                                {item.finding.name || 'Linked Finding'}
                                              </Text>
                                              {item.finding.severity && (
                                                <Badge
                                                  backgroundColor={getSeverityBackgroundColor(item.finding.severity)}
                                                  color={getSeverityTextColor(item.finding.severity)}
                                                  style={{ 
                                                    fontWeight: 600, 
                                                    fontSize: '8px',
                                                    padding: '1px 4px',
                                                    border: `1px solid ${getSeverityTextColor(item.finding.severity)}`,
                                                    display: 'inline-block',
                                                    lineHeight: '1.2'
                                                  }}
                                                >
                                                  {item.finding.severity.toUpperCase()}
                                                </Badge>
                                              )}
                                            </Pane>
                                            <IconButton
                                              icon={CrossIcon}
                                              size={10}
                                              appearance="minimal"
                                              height={14}
                                              width={14}
                                              padding={0}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleUnlinkFindingWithScroll(item, item.finding.id);
                                              }}
                                              title="Unlink finding"
                                            />
                                          </Pane>
                                        )}
                                      </Pane>
                                    )}
                                    {item.finding_id && !item.finding && (!item.findings || item.findings.length === 0) && (
                                      <Card
                                        backgroundColor="#F9FAFB"
                                        border="1px solid #E5E7EB"
                                        padding={10}
                                        marginTop={8}
                                        width="100%"
                                        position="relative"
                                      >
                                        <Pane display="flex" justifyContent="space-between" alignItems="center">
                                          <Text size={400} fontWeight={600} color="#111827">
                                            Finding #{item.finding_id.substring(0, 8)}
                                          </Text>
                                          <IconButton
                                            icon={CrossIcon}
                                            size={16}
                                            appearance="minimal"
                                            height={24}
                                            width={24}
                                            padding={0}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUnlinkFindingWithScroll(item);
                                            }}
                                            title="Unlink finding"
                                          />
                                        </Pane>
                                      </Card>
                                    )}
                                  </Pane>
                                  
                                  {/* Notes Section */}
                                  {isEditing ? (
                                    <Pane display="flex" flexDirection="column" gap={4}>
                                      <Textarea
                                        value={wstgItemNotes}
                                        onChange={(e) => setWstgItemNotes(e.target.value)}
                                        placeholder="Enter notes..."
                                        rows={3}
                                        width="100%"
                                      />
                                      <Pane display="flex" gap={4}>
                                        <Button
                                          size="small"
                                          appearance="primary"
                                          onClick={() => handleSaveNotes(item)}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setEditingWstgItem(null);
                                            setWstgItemNotes('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </Pane>
                                    </Pane>
                                  ) : (
                                    <Pane display="flex" flexDirection="column" gap={4}>
                                      {item.notes && (
                                        <Text size={300} color="#6B7280" style={{ fontWeight: 400 }}>
                                          {item.notes}
                                        </Text>
                                      )}
                                      <Pane display="flex" justifyContent="center">
                                        <Button
                                          size="small"
                                          appearance="default"
                                          iconBefore={EditIcon}
                                          onClick={() => {
                                            setEditingWstgItem(item.test_id);
                                            setWstgItemNotes(item.notes || '');
                                          }}
                                          style={{ 
                                            border: '1px solid #D1D5DB',
                                            backgroundColor: '#FFFFFF',
                                            color: '#374151'
                                          }}
                                        >
                                          {item.notes ? 'Edit Notes' : 'Add Notes'}
                                        </Button>
                                      </Pane>
                                    </Pane>
                                  )}
                                </Pane>
                                
                                {/* Right side: Action Buttons in column */}
                                <Pane display="flex" flexDirection="column" gap={4} alignItems="flex-end">
                                  {item.status === null || item.status === undefined ? (
                                    <>
                                      <Button
                                        size="small"
                                        appearance="default"
                                        intent="success"
                                        onClick={() => handleUpdateStatus('OK.', item)}
                                        width="100%"
                                      >
                                        Mark OK
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="default"
                                        intent="danger"
                                        onClick={() => handleUpdateStatus('Vulnerability found', item)}
                                        width="100%"
                                      >
                                        Vulnerability Found
                                      </Button>
                                    </>
                                  ) : item.status === 'OK.' ? (
                                    <>
                                      <Button
                                        size="small"
                                        appearance="default"
                                        intent="danger"
                                        onClick={() => handleUpdateStatus('Vulnerability found', item)}
                                        width="100%"
                                      >
                                        Mark as Vulnerability
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="minimal"
                                        onClick={() => handleUpdateStatus(null, item)}
                                        width="100%"
                                      >
                                        Reset to Not Tested
                                      </Button>
                                    </>
                                  ) : item.status === 'Vulnerability found' ? (
                                    <>
                                      <Button
                                        size="small"
                                        appearance="default"
                                        intent="success"
                                        onClick={() => handleUpdateStatus('OK.', item)}
                                        width="100%"
                                      >
                                        Mark as OK
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="minimal"
                                        onClick={() => handleUpdateStatus(null, item)}
                                        width="100%"
                                      >
                                        Reset to Not Tested
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        size="small"
                                        appearance="default"
                                        intent="success"
                                        onClick={() => handleUpdateStatus('OK.', item)}
                                        width="100%"
                                      >
                                        Mark OK
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="default"
                                        intent="danger"
                                        onClick={() => handleUpdateStatus('Vulnerability found', item)}
                                        width="100%"
                                      >
                                        Mark as Vulnerability
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="minimal"
                                        onClick={() => handleUpdateStatus(null, item)}
                                        width="100%"
                                      >
                                        Reset to Not Tested
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    size="small"
                                    appearance="primary"
                                    onClick={() => handleOpenCreateFinding(item)}
                                    width="100%"
                                  >
                                    Create Finding
                                  </Button>
                                  <Button
                                    size="small"
                                    appearance="default"
                                    onClick={() => handleOpenLinkFindingWithScroll(item)}
                                    width="100%"
                                  >
                                    Link Finding
                                  </Button>
                                </Pane>
                              </Pane>
                            </Pane>
                          </Pane>
                        );
                      })}
                    </Pane>
                  </Pane>
                </>
              ) : (
                <Pane padding={32} textAlign="center">
                  <Text color="muted">No WSTG checklist data available</Text>
                </Pane>
              )}
            </Pane>
          )}
        </Pane>
      ) : (
        <Pane padding={32} textAlign="center" flex={1} display="flex" alignItems="center" justifyContent="center">
          <Text color="muted">No overview data available</Text>
        </Pane>
      )}
      
      {/* Dialogs */}
      <Dialog
        isShown={showDeleteNoteDialog}
        title="Delete Note"
        intent="danger"
        onCloseComplete={() => setShowDeleteNoteDialog(false)}
        confirmLabel="Delete"
        onConfirm={handleDeleteNote}
      >
        Are you sure you want to delete this note? This action cannot be undone.
      </Dialog>

      <Dialog
        isShown={showLinkFindingDialog}
        title="Link Finding to WSTG Test"
        onCloseComplete={() => {
          setShowLinkFindingDialog(false);
        }}
        confirmLabel="Cancel"
        hasCancel={false}
        hasClose={true}
        shouldCloseOnEscapePress={true}
        shouldCloseOnOverlayClick={true}
        containerProps={{
          style: {
            zIndex: 10001,
            position: 'fixed'
          }
        }}
        overlayProps={{
          style: {
            zIndex: 10000
          }
        }}
      >
        {linkFindingItem && (
          <Pane>
            <Text size={400} marginBottom={16}>
              Select a finding to link to <strong>{linkFindingItem.test_id}: {linkFindingItem.test_name}</strong>
            </Text>
            
            {findingsLoading ? (
              <Pane display="flex" justifyContent="center" padding={32}>
                <Spinner />
              </Pane>
            ) : availableFindings.length === 0 ? (
              <Pane padding={32} textAlign="center">
                <Text color="muted">No findings available to link</Text>
              </Pane>
            ) : (
              <Pane maxHeight="400px" overflowY="auto" border="1px solid #E6E8F0" borderRadius={4}>
                {availableFindings.map((finding) => (
                  <Pane
                    key={finding.id}
                    padding={12}
                    borderBottom="1px solid #E6E8F0"
                    cursor="pointer"
                    onClick={() => handleLinkFindingWithScroll(finding.id)}
                  >
                    <Text size={400} fontWeight={500}>{finding.name}</Text>
                    <Badge color={finding.severity === 'critical' ? 'red' : 'default'}>
                      {finding.severity}
                    </Badge>
                  </Pane>
                ))}
              </Pane>
            )}
          </Pane>
        )}
      </Dialog>
      
      {/* Screenshot Modal */}
      {selectedScreenshot && (
        <Dialog
          isShown={!!selectedScreenshot}
          title="Screenshot"
          onCloseComplete={() => setSelectedScreenshot(null)}
          hasFooter={false}
          width="90vw"
          maxWidth="1200px"
        >
          <Pane padding={16}>
            <Image
              src={selectedScreenshot.screenshot_url || selectedScreenshot.thumbnail_url}
              alt="Full screenshot"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          </Pane>
        </Dialog>
      )}
    </Pane>
  );
};

export default EntityOverviewPanel;

